import React, { useState, useEffect, useRef } from 'react';
import { RiRefreshLine, RiArrowLeftLine, RiArrowRightLine, RiCheckLine } from 'react-icons/ri';
import KitsuMark from '../brand/KitsuMark';

/* ══════════════════════════════════════════════════════════════
   SESSION COMPLETE

   The screen you land on after finishing a step. It used to be a
   number and two buttons — correct, and completely flat.

   What makes the reference version feel like a reward is not the
   confetti; it is three specific things:

     · the shapes sit on a ground shadow, so they read as objects in a
       space rather than graphics pasted on a background
     · they hop and tumble continuously, out of phase with each other
     · the score counts up, with sparks, rather than simply appearing

   Staged so it arrives in order — mark, then verdict, then score —
   because everything at once reads as a page that merely loaded.
══════════════════════════════════════════════════════════════ */

const COUNT_MS = 850;

function useCountUp(target, delay) {
  const [value, setValue] = useState(0);
  const raf = useRef(0);

  useEffect(() => {
    if (!target) { setValue(0); return undefined; }
    let start = 0;
    let stopped = false;
    const timer = setTimeout(() => {
      const step = now => {
        if (stopped) return;
        if (!start) start = now;
        const t = Math.min(1, (now - start) / COUNT_MS);
        setValue(Math.round(target * (1 - Math.pow(1 - t, 3))));
        if (t < 1) raf.current = requestAnimationFrame(step);
      };
      raf.current = requestAnimationFrame(step);
    }, delay);

    /* rAF does not run in a backgrounded tab, and a count-up driven only by
       rAF therefore sits at 0 forever — the learner comes back to a session
       that says they scored nothing. This lands the final number regardless,
       comfortably after the animation would have finished on its own; the
       animated path sets the same value first, so it is a no-op when frames
       were actually delivered. */
    const settle = setTimeout(() => { if (!stopped) setValue(target); }, delay + COUNT_MS + 60);

    return () => {
      stopped = true;
      clearTimeout(timer);
      clearTimeout(settle);
      cancelAnimationFrame(raf.current);
    };
  }, [target, delay]);

  const reduce = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  return reduce ? target : value;
}

export default function SessionComplete({
  scorePct,
  passed,
  title,
  detail,
  correct,
  total,
  label,
  onRetry,
  onExit,
  onNext,
  retryLabel = 'Practise again',
  exitLabel = 'Back to path',
  nextLabel = 'Continue',
  /* How far through the row this leaves you, e.g. "3/4". Sits on the
     button rather than only in the detail line, because the button is
     what the eye goes to and "continue to what, exactly?" is the
     question it has to answer on its own. */
  nextProgress,
}) {
  /* A percentage where there is one, the raw count otherwise — a flashcard
     run has no score, and counting up to NaN% helps nobody. */
  const hasPct = typeof scorePct === 'number';
  const shown = useCountUp(hasPct ? scorePct : (correct || 0), 520);

  return (
    <div className="sess-done">
      {/* The stage: a mark hopping on its own shadow, with a token that
          tumbles around it. The shadow is what grounds the whole thing. */}
      <div className="sess-stage" aria-hidden="true">
        <span className="sess-shadow" />

        <span className="sess-token">
          <span className="sess-token-inner">
            {passed === false ? <RiRefreshLine size={22} /> : <RiCheckLine size={24} />}
          </span>
        </span>

        <span className="sess-hop">
          <KitsuMark size={92} interactive={false} glow={false} />
        </span>
      </div>

      <div className={`sess-score${passed === false ? ' miss' : ''}`}>
        {shown}{hasPct && <span>%</span>}
        {passed !== false && (
          <i className="sess-sparks">
            {[...Array(6)].map((_, i) => <b key={i} style={{ '--a': `${i * 60 + 12}deg` }} />)}
          </i>
        )}
      </div>

      <h2 className="sess-title">{title}</h2>
      {total > 0 && <p className="sess-sub">{correct} / {total} {label}</p>}
      {detail && <p className="sess-detail">{detail}</p>}

      {/* Only the actions the caller actually has. A flashcard session run
          from the sidebar has nowhere to go "back" to, and rendering a dead
          second button just to keep the row symmetrical is worse than one.

          When there is a next step, it takes the primary slot and going
          back becomes secondary. A row is four exercises; making the
          learner return to the path and hunt for the node they are
          standing on, four times per row, was the wrong default. */}
      <div className={`sess-actions${onNext ? ' has-next' : ''}`}>
        {onRetry && (
          <button className={(onExit || onNext) ? 'btn-ghost' : 'btn-primary'} onClick={onRetry}>
            <RiRefreshLine /> {retryLabel}
          </button>
        )}
        {onExit && (
          <button className={onNext ? 'btn-ghost' : 'btn-primary'} onClick={onExit}>
            <RiArrowLeftLine /> {exitLabel}
          </button>
        )}
        {onNext && (
          <button className="btn-primary sess-next" onClick={onNext}>
            {nextLabel}
            {nextProgress && <span className="sess-next-count">{nextProgress}</span>}
            <RiArrowRightLine />
          </button>
        )}
      </div>
    </div>
  );
}
